import { Alert } from '@fountain/fountain-ui-components';
import {
  WorkflowSchedulerStage,
  WorkflowSchedulerV2Stage,
} from 'api-clients/monolith';
import React, { useContext, VFC } from 'react';
import { useSelector } from 'react-redux';

import {
  makeAvailableSlotsNewAvailabilityMessage,
  makeScoreCardTemplatesEnabled,
  makeUniversalRecruiterDashboardEnabled,
} from 'containers/Auth_old/selectors';
import { AdvancedSettings } from 'containers/WorkflowEditor/components/AdvancedSettings';
import { ReviewStage } from 'containers/WorkflowEditor/components/ReviewStage';
import { StageSection } from 'containers/WorkflowEditor/components/StageSection';
import { StageContext } from 'containers/WorkflowEditor/contexts/stageContext';

import { IdleMoveRule } from '../../../IdleMoveRule';
import { StageSettingMessageCard } from '../../../StageSettingMessageCard';
import { ScorecardCard } from '../ScorecardCard';
import { ExternalCalendarCard } from './ExternalCalendarCard';
import { GoEnablementSetting } from './GoEnablementSetting';
import { LiveVideoCard } from './LiveVideoCard';
import { OnsiteAddressCard } from './OnsiteAddressCard';
import { SchedulerCard } from './SchedulerCard';
import { SessionHostsCardContainer } from './SchedulerUsersCard';
import { SessionDetailsCardContainer } from './SessionDetailsCard/SessionDetailsCard';

export interface SchedulerStageZeroProps {
  stage: WorkflowSchedulerV2Stage;
  address: string;
  openingExternalId: string;
}

export const SchedulerStageZero: VFC<SchedulerStageZeroProps> = ({
  stage,
  address,
  openingExternalId,
}) => {
  const { setStage } = useContext(StageContext);
  const isSubstage = Boolean(stage.parent_stage_id);
  const universalRecruiterDashboardEnabled = useSelector(
    makeUniversalRecruiterDashboardEnabled(),
  );
  const scoreCardTemplatesEnabled = useSelector(
    makeScoreCardTemplatesEnabled(),
  );

  return (
    <div>
      {universalRecruiterDashboardEnabled && (
        <StageSection>
          <ReviewStage stage={stage} />
        </StageSection>
      )}
      <StageSection>
        <SessionDetailsCardContainer
          stage={stage}
          setStage={setStage}
          address={address}
        />
      </StageSection>
      <StageSection>
        <SessionHostsCardContainer setStage={setStage} stage={stage} />
      </StageSection>
      <StageSection>
        <SchedulerCard
          stage={stage}
          setStage={setStage}
          openingExternalId={openingExternalId}
        />
      </StageSection>
      {scoreCardTemplatesEnabled && (
        <StageSection>
          <ScorecardCard stage={stage} setStage={setStage} />
        </StageSection>
      )}
      <StageSection>
        <LiveVideoCard stage={stage} setStage={setStage} />
      </StageSection>
      {!isSubstage && (
        <>
          <StageSection>
            <IdleMoveRule stage={stage} />
          </StageSection>
          <StageSection>
            <StageSettingMessageCard
              messageType="scheduler_v2"
              stageExternalId={stage.external_id}
            />
          </StageSection>
        </>
      )}
      <StageSection>
        <StageSettingMessageCard
          messageType="confirmation"
          stageExternalId={stage.external_id}
        />
      </StageSection>
      <StageSection>
        <StageSettingMessageCard
          messageType="cancellation_v2"
          stageExternalId={stage.external_id}
        />
      </StageSection>
      {!isSubstage && (
        <>
          <StageSection>
            <StageSettingMessageCard
              messageType="reminder"
              stageExternalId={stage.external_id}
            />
          </StageSection>
          <StageSection>
            <StageSettingMessageCard
              messageType="trigger"
              stageExternalId={stage.external_id}
            />
          </StageSection>
        </>
      )}
    </div>
  );
};

export interface SchedulerStageProps {
  stage: WorkflowSchedulerStage;
  openingExternalId: string;
}

export const SchedulerStage: VFC<SchedulerStageProps> = ({
  stage,
  openingExternalId,
}) => {
  const { setStage, updateStageResult } = useContext(StageContext);
  const availableSlotsNewAvailabilityMessage = useSelector(
    makeAvailableSlotsNewAvailabilityMessage(),
  );
  const universalRecruiterDashboardEnabled = useSelector(
    makeUniversalRecruiterDashboardEnabled(),
  );
  const scoreCardTemplatesEnabled = useSelector(
    makeScoreCardTemplatesEnabled(),
  );

  const isSubstage = Boolean(stage.parent_stage_id);

  const hasError =
    updateStageResult.isError &&
    updateStageResult.error?.errors
      ?.more_than_one_scheduler_stage_with_hire_go_interview_hosts !==
      undefined;

  return (
    <div data-testid="schedulerStage">
      {updateStageResult.isError &&
        updateStageResult.error?.errors?.base?.map(error => (
          <StageSection key={error}>
            <Alert fullWidth type="danger">
              <span>{error}</span>
            </Alert>
          </StageSection>
        ))}
      {universalRecruiterDashboardEnabled && (
        <StageSection>
          <ReviewStage stage={stage} />
        </StageSection>
      )}
      {!stage?.additional_info?.connected_to_external_calendar && (
        <StageSection>
          <ExternalCalendarCard />
        </StageSection>
      )}
      {stage.account_go_enabled && (
        <StageSection>
          <GoEnablementSetting
            stage={stage}
            setStage={setStage}
            hasError={hasError}
          />
        </StageSection>
      )}
      <StageSection>
        <SchedulerCard
          stage={stage}
          setStage={setStage}
          openingExternalId={openingExternalId}
        />
      </StageSection>
      {scoreCardTemplatesEnabled && (
        <StageSection>
          <ScorecardCard stage={stage} setStage={setStage} />
        </StageSection>
      )}
      <StageSection>
        <LiveVideoCard stage={stage} setStage={setStage} />
      </StageSection>
      <StageSection>
        <OnsiteAddressCard stage={stage} setStage={setStage} />
      </StageSection>
      {!isSubstage && (
        <>
          <StageSection>
            <IdleMoveRule stage={stage} />
          </StageSection>
          <StageSection>
            <StageSettingMessageCard
              messageType="scheduler"
              stageExternalId={stage.external_id}
            />
          </StageSection>
        </>
      )}
      <StageSection>
        <StageSettingMessageCard
          messageType="confirmation"
          stageExternalId={stage.external_id}
        />
      </StageSection>
      <StageSection>
        <StageSettingMessageCard
          messageType="cancellation"
          stageExternalId={stage.external_id}
        />
      </StageSection>
      {!isSubstage && (
        <>
          <StageSection>
            <StageSettingMessageCard
              messageType="reminder"
              stageExternalId={stage.external_id}
            />
          </StageSection>
          <StageSection>
            <StageSettingMessageCard
              messageType="trigger"
              stageExternalId={stage.external_id}
            />
          </StageSection>
        </>
      )}
      {availableSlotsNewAvailabilityMessage && (
        <StageSection>
          <StageSettingMessageCard
            messageType="availability"
            stageExternalId={stage.external_id}
          />
        </StageSection>
      )}
      {stage.account_go_enabled && (
        <StageSection>
          <AdvancedSettings stage={stage} />
        </StageSection>
      )}
    </div>
  );
};
